import React, { useState, useEffect } from 'react';
import { 
  Copy, AlertTriangle, CheckCircle, Image, Type, Merge, 
  Eye, Edit2, Trash2, RefreshCw, Loader2, ChevronDown,
  ChevronRight, Package, X, Check, ArrowRight, Search
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const IdenticalProductsPortal = () => {
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [selectedForMerge, setSelectedForMerge] = useState({});
  const [processingMerge, setProcessingMerge] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState({ total_groups: 0, total_duplicate_products: 0 });

  // Fetch duplicate products
  const fetchDuplicates = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/products/duplicates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setDuplicateGroups(data.duplicate_groups || []);
        setStats({
          total_groups: data.total_groups || 0,
          total_duplicate_products: data.total_duplicate_products || 0
        });
        
        // Auto-expand first few groups
        const expanded = {};
        data.duplicate_groups?.slice(0, 3).forEach(g => {
          expanded[g.group_id] = true;
        });
        setExpandedGroups(expanded);
      } else {
        toast.error('Failed to fetch duplicate products');
      }
    } catch (error) {
      console.error('Error fetching duplicates:', error);
      toast.error('Error loading duplicate products');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDuplicates();
  }, []);

  // Toggle group expansion
  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  // Handle product selection for merge
  const toggleProductSelection = (groupId, productId, isKeep = false) => {
    setSelectedForMerge(prev => {
      const groupSelection = prev[groupId] || { keep: null, merge: [] };
      
      if (isKeep) {
        return {
          ...prev,
          [groupId]: { ...groupSelection, keep: productId }
        };
      } else {
        const mergeList = groupSelection.merge.includes(productId)
          ? groupSelection.merge.filter(id => id !== productId)
          : [...groupSelection.merge, productId];
        return {
          ...prev,
          [groupId]: { ...groupSelection, merge: mergeList }
        };
      }
    });
  };

  // Merge selected products
  const handleMerge = async (groupId) => {
    const selection = selectedForMerge[groupId];
    if (!selection?.keep || selection.merge.length === 0) {
      toast.error('Please select one product to keep and at least one to merge');
      return;
    }

    setProcessingMerge(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/products/merge?keep_product_id=${selection.keep}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(selection.merge)
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        // Refresh the list
        fetchDuplicates();
        // Clear selection
        setSelectedForMerge(prev => {
          const newState = { ...prev };
          delete newState[groupId];
          return newState;
        });
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to merge products');
      }
    } catch (error) {
      console.error('Merge error:', error);
      toast.error('Error merging products');
    } finally {
      setProcessingMerge(false);
    }
  };

  // Mark product as reviewed (not a duplicate)
  const markAsReviewed = async (productId) => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/products/${productId}/mark-reviewed`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Product marked as reviewed');
        fetchDuplicates();
      } else {
        toast.error('Failed to mark as reviewed');
      }
    } catch (error) {
      toast.error('Error marking product');
    }
  };

  // Get match type badge color
  const getMatchTypeBadge = (matchType) => {
    switch (matchType) {
      case 'name':
        return <Badge className="bg-purple-100 text-purple-700"><Type className="w-3 h-3 mr-1" />Exact Name</Badge>;
      case 'name_similar':
        return <Badge className="bg-blue-100 text-blue-700"><Type className="w-3 h-3 mr-1" />Similar Name</Badge>;
      case 'image':
        return <Badge className="bg-green-100 text-green-700"><Image className="w-3 h-3 mr-1" />Same Image</Badge>;
      case 'both':
        return <Badge className="bg-red-100 text-red-700"><Copy className="w-3 h-3 mr-1" />Name & Image</Badge>;
      default:
        return <Badge variant="outline">{matchType}</Badge>;
    }
  };

  // Filter groups by search
  const filteredGroups = duplicateGroups.filter(group => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return group.products.some(p => 
      p.name?.toLowerCase().includes(query) ||
      p.category?.toLowerCase().includes(query) ||
      p.barcode?.includes(query)
    );
  });

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500 mr-3" />
          <span className="text-gray-600">Analyzing products for duplicates...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 flex items-center gap-2">
            <Copy className="w-7 h-7 text-orange-500" />
            Identical Products Portal
          </h1>
          <p className="text-gray-500 mt-1">
            AI-detected duplicate and similar products for review
          </p>
        </div>
        <Button onClick={fetchDuplicates} variant="outline" className="flex items-center gap-2">
          <RefreshCw className="w-4 h-4" />
          Refresh Analysis
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Card className="p-4 bg-gradient-to-br from-orange-50 to-red-50 border-orange-200">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-orange-500 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-orange-700">{stats.total_groups}</p>
              <p className="text-sm text-orange-600">Duplicate Groups</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-4 bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-200">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-purple-500 flex items-center justify-center">
              <Package className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-purple-700">{stats.total_duplicate_products}</p>
              <p className="text-sm text-purple-600">Products to Review</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-700">Active</p>
              <p className="text-sm text-green-600">Products remain on sale</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search duplicate groups..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Products remain on sale during review</p>
            <p className="text-blue-600">
              All products listed here are still available for purchase. Review each group and either 
              merge duplicates (keeping one) or mark them as reviewed if they are different products.
            </p>
          </div>
        </div>
      </div>

      {/* Duplicate Groups */}
      {filteredGroups.length === 0 ? (
        <Card className="p-12 text-center">
          <CheckCircle className="w-16 h-16 text-green-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-800 mb-2">No Duplicates Found!</h3>
          <p className="text-gray-500">Your product catalog looks clean. No identical or similar products detected.</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredGroups.map((group) => {
            const isExpanded = expandedGroups[group.group_id];
            const selection = selectedForMerge[group.group_id] || { keep: null, merge: [] };
            
            return (
              <Card key={group.group_id} className="overflow-hidden">
                {/* Group Header */}
                <div 
                  className="p-4 bg-gray-50 border-b cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => toggleGroup(group.group_id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {isExpanded ? (
                        <ChevronDown className="w-5 h-5 text-gray-500" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-500" />
                      )}
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-gray-800">
                            {group.products.length} Products
                          </span>
                          {getMatchTypeBadge(group.match_type)}
                          <Badge variant="outline" className="text-xs">
                            {Math.round(group.similarity_score * 100)}% Match
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">{group.match_reason}</p>
                      </div>
                    </div>
                    
                    {/* Preview Images */}
                    <div className="flex -space-x-2">
                      {group.products.slice(0, 4).map((product, idx) => (
                        <div 
                          key={product.id}
                          className="w-10 h-10 rounded-full border-2 border-white bg-gray-200 overflow-hidden"
                          style={{ zIndex: 4 - idx }}
                        >
                          {product.image ? (
                            <img 
                              src={product.image} 
                              alt={product.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <Package className="w-5 h-5 m-2.5 text-gray-400" />
                          )}
                        </div>
                      ))}
                      {group.products.length > 4 && (
                        <div className="w-10 h-10 rounded-full border-2 border-white bg-gray-300 flex items-center justify-center text-xs font-medium text-gray-600">
                          +{group.products.length - 4}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Expanded Content */}
                {isExpanded && (
                  <div className="p-4">
                    {/* Instructions */}
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm">
                      <p className="text-amber-800">
                        <strong>To merge:</strong> Select one product to <span className="text-green-600 font-medium">KEEP</span> (green), 
                        then select others to <span className="text-red-600 font-medium">MERGE</span> (red). 
                        Merged products will be archived.
                      </p>
                    </div>
                    
                    {/* Products Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                      {group.products.map((product) => {
                        const isSelectedKeep = selection.keep === product.id;
                        const isSelectedMerge = selection.merge.includes(product.id);
                        
                        return (
                          <div 
                            key={product.id}
                            className={`border-2 rounded-lg p-3 transition-all ${
                              isSelectedKeep 
                                ? 'border-green-500 bg-green-50' 
                                : isSelectedMerge 
                                  ? 'border-red-500 bg-red-50'
                                  : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            {/* Product Image */}
                            <div className="aspect-square rounded-lg overflow-hidden bg-gray-100 mb-3">
                              {product.image ? (
                                <img 
                                  src={product.image} 
                                  alt={product.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Package className="w-12 h-12 text-gray-300" />
                                </div>
                              )}
                            </div>
                            
                            {/* Product Info */}
                            <h4 className="font-medium text-gray-800 mb-1 line-clamp-2">{product.name}</h4>
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-lg font-bold text-green-600">${product.price?.toFixed(2)}</span>
                              <Badge variant="outline" className="text-xs">{product.category}</Badge>
                            </div>
                            {product.barcode && (
                              <p className="text-xs text-gray-500 mb-2">Barcode: {product.barcode}</p>
                            )}
                            
                            {/* Selection Status */}
                            {isSelectedKeep && (
                              <div className="bg-green-100 text-green-700 text-xs font-medium px-2 py-1 rounded mb-2 flex items-center gap-1">
                                <Check className="w-3 h-3" /> KEEP THIS PRODUCT
                              </div>
                            )}
                            {isSelectedMerge && (
                              <div className="bg-red-100 text-red-700 text-xs font-medium px-2 py-1 rounded mb-2 flex items-center gap-1">
                                <X className="w-3 h-3" /> WILL BE ARCHIVED
                              </div>
                            )}
                            
                            {/* Action Buttons */}
                            <div className="flex gap-2 mt-3">
                              <Button
                                size="sm"
                                variant={isSelectedKeep ? "default" : "outline"}
                                className={isSelectedKeep ? "bg-green-500 hover:bg-green-600 flex-1" : "flex-1"}
                                onClick={() => toggleProductSelection(group.group_id, product.id, true)}
                              >
                                <Check className="w-3 h-3 mr-1" />
                                Keep
                              </Button>
                              <Button
                                size="sm"
                                variant={isSelectedMerge ? "destructive" : "outline"}
                                className="flex-1"
                                onClick={() => toggleProductSelection(group.group_id, product.id, false)}
                                disabled={isSelectedKeep}
                              >
                                <Merge className="w-3 h-3 mr-1" />
                                Merge
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Action Bar */}
                    <div className="flex flex-wrap gap-3 pt-4 border-t">
                      <Button
                        onClick={() => handleMerge(group.group_id)}
                        disabled={!selection.keep || selection.merge.length === 0 || processingMerge}
                        className="bg-orange-500 hover:bg-orange-600"
                      >
                        {processingMerge ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Merge className="w-4 h-4 mr-2" />
                        )}
                        Merge Selected ({selection.merge.length})
                      </Button>
                      
                      <Button
                        variant="outline"
                        onClick={() => {
                          // Mark all as reviewed
                          group.products.forEach(p => markAsReviewed(p.id));
                        }}
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Mark All as Reviewed
                      </Button>
                      
                      <Button
                        variant="ghost"
                        onClick={() => setSelectedForMerge(prev => {
                          const newState = { ...prev };
                          delete newState[group.group_id];
                          return newState;
                        })}
                      >
                        <X className="w-4 h-4 mr-2" />
                        Clear Selection
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default IdenticalProductsPortal;
